import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, CheckCircle, AlertTriangle } from "lucide-react";

export default function SetupAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    document.title = "Setup Account — Smartorange HUB";
  }, []);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-zinc-50 mb-2">Invalid Link</h2>
          <p className="text-zinc-400">This invite link is invalid or expired. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("token", token);
      formData.append("password", password);
      
      const res = await api.post("/auth/setup-account", formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      setEmail(res.data.email);
      setDone(true);
      toast.success("Account activated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate account");
    }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-zinc-50 mb-2">Account Activated!</h2>
          <p className="text-zinc-400 mb-6">Your password has been set. You can now log in with <strong className="text-zinc-50">{email}</strong></p>
          <button
            data-testid="go-to-login-btn"
            onClick={() => navigate("/login")}
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="bg-[#141414] border border-[#262626] rounded-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-orange-400" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-50 mb-1">
            <span>SMART</span><span className="text-orange-400">ORANGE</span> <span className="text-zinc-500 text-lg">HUB</span>
          </h1>
          <p className="text-zinc-400 text-sm mt-2">Set your password to activate your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">Password</label>
            <div className="relative">
              <input
                data-testid="setup-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#333] rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-orange-500 pr-10"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">Confirm Password</label>
            <input
              data-testid="setup-confirm-password-input"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat password"
              className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#333] rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-orange-500"
              required
            />
          </div>

          {password && confirmPassword && password !== confirmPassword && (
            <p className="text-red-400 text-xs">Passwords don't match</p>
          )}

          <button
            data-testid="setup-submit-btn"
            type="submit"
            disabled={loading || password.length < 6 || password !== confirmPassword}
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors mt-2"
          >
            {loading ? "Activating..." : "Activate Account"}
          </button>
        </form>
      </div>
    </div>
  );
}
